import React, {
  useMemo,
  useImperativeHandle,
  useRef,
  forwardRef,
  useId,
} from "react";
import { Label } from "../Label";
import { Button } from "../Button";
import * as styles from "./styles";
import CloseIcon from "~/components/deprecated/assets/close.svg";

export interface InputProps {
  name?: string;
  value?: string;
  label?: string;
  placeholder?: string;
  className?: string;
  autofocus?: boolean;
  hasError?: boolean;
  adornment?: React.ReactElement;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onClear?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface InputRef {
  focus: () => void;
  input: HTMLInputElement | null;
  container: HTMLDivElement | null;
}

export const Input = forwardRef<InputRef, InputProps>(
  (
    {
      name,
      value,
      label,
      placeholder,
      className,
      autofocus = false,
      hasError = false,
      adornment,
      onBlur,
      onFocus,
      onClear,
      onChange,
    },
    ref,
  ) => {
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef?.current?.focus();
      },
      get input() {
        return inputRef.current;
      },
      get container() {
        return inputContainerRef.current;
      },
    }));

    const renderLabel = useMemo(() => {
      if (!label) {
        return null;
      }

      return (
        <Label htmlFor={inputId} style={{ lineHeight: "26px" }}>
          {label}
        </Label>
      );
    }, [label]);

    return (
      <div className={styles.container} ref={inputContainerRef}>
        {renderLabel}
        <div className={styles.innerContainer} data-has-error={hasError}>
          {adornment && <span className={styles.adornment}>{adornment}</span>}
          <input
            type="text"
            className={`${className} ${styles.input}`}
            ref={inputRef}
            name={name}
            autoFocus={autofocus}
            id={inputId}
            value={value}
            onBlur={onBlur}
            onFocus={onFocus}
            onChange={onChange}
            placeholder={placeholder}
            style={adornment && { width: "-webkit-fill-available" }}
          />
          {onClear && (
            <Button size="small" variant="text" onClick={onClear}>
              <CloseIcon />
            </Button>
          )}
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";
